/**
 * DeckGridView - Grid layout for deck cards with grouping support
 */
import type { ReactElement } from 'react';
import { useState } from 'react';

import type { DeckFormat, DeckStatus } from '@/types/deck';
import useCardGroups, { CardGroup } from '../hooks/useCardGroups';
import { EnrichedDeckCard } from '../types';
import CommanderSection from './CommanderSection';
import DeckGridViewCard from './DeckGridViewCard';
import SectionTitle from './SectionTitle';

export interface DeckGridViewProps {
  deckId: string;
  cards: EnrichedDeckCard[];
  commanderCards?: EnrichedDeckCard[];
  companionCards?: EnrichedDeckCard[];
  isLocked?: boolean;
  deckFormat?: DeckFormat | null;
  deckStatus: DeckStatus;
  /** Scryfall ID of the current deck cover card */
  coverCardId: string | null;
  onSetCover: (scryfallId: string) => void;
  onSetCommander: (cardId: string, scryfallId: string) => void;
  /** Called when a card is selected to open details panel */
  onCardSelect?: (scryfallId: string, deckCardId: string) => void;
  /** Whether multi-select mode is active */
  isSelectionMode?: boolean;
  /** Set of selected deck card ids */
  selectedDeckCardIds?: Set<string>;
  /** Toggle selection for a deck card */
  onToggleDeckCard?: (deckCardId: string) => void;
}

export default function DeckGridView({
  deckId,
  cards,
  commanderCards = [],
  companionCards = [],
  isLocked = false,
  deckFormat,
  deckStatus,
  coverCardId,
  onSetCover,
  onSetCommander,
  onCardSelect,
  isSelectionMode = false,
  selectedDeckCardIds,
  onToggleDeckCard,
}: DeckGridViewProps): ReactElement {
  const cardGroups = useCardGroups(cards);

  return (
    <div className="space-y-8">
      {/* Commander and Companion Section - prominent display at top */}
      {(commanderCards.length > 0 || companionCards.length > 0) && (
        <CommanderSection
          deckId={deckId}
          commanders={commanderCards}
          companions={companionCards}
          isLocked={isLocked}
          deckFormat={deckFormat}
          variant="grid"
          onCardSelect={onCardSelect}
        />
      )}

      {cardGroups.map((group) => (
        <CardSection
          key={group.key}
          deckId={deckId}
          group={group}
          isLocked={isLocked}
          deckFormat={deckFormat}
          deckStatus={deckStatus}
          coverCardId={coverCardId}
          onSetCover={onSetCover}
          onSetCommander={onSetCommander}
          onCardSelect={onCardSelect}
          isSelectionMode={isSelectionMode}
          selectedDeckCardIds={selectedDeckCardIds}
          onToggleDeckCard={onToggleDeckCard}
        />
      ))}
    </div>
  );
}

interface CardSectionProps {
  deckId: string;
  group: CardGroup<EnrichedDeckCard>;
  isLocked?: boolean;
  deckFormat?: DeckFormat | null;
  deckStatus: DeckStatus;
  coverCardId: string | null;
  onSetCover: (scryfallId: string) => void;
  onSetCommander: (cardId: string, scryfallId: string) => void;
  onCardSelect?: (scryfallId: string, deckCardId: string) => void;
  isSelectionMode?: boolean;
  selectedDeckCardIds?: Set<string>;
  onToggleDeckCard?: (deckCardId: string) => void;
}

function CardSection({
  deckId,
  group,
  isLocked,
  deckFormat,
  deckStatus,
  coverCardId,
  onSetCover,
  onSetCommander,
  onCardSelect,
  isSelectionMode = false,
  selectedDeckCardIds,
  onToggleDeckCard,
}: CardSectionProps) {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <section>
      {/* Group Header */}
      <SectionTitle
        title={group.label}
        uniqueCount={group.cards.length}
        totalCount={group.totalQuantity}
        onClick={() => setIsOpen(!isOpen)}
        isExpanded={isOpen}
      />

      {/* Card Grid */}
      {isOpen && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7">
          {group.cards.map((deckCard) =>
            deckCard.card ? (
              <DeckGridViewCard
                key={deckCard.id}
                deckId={deckId}
                card={deckCard}
                isLocked={isLocked}
                deckFormat={deckFormat}
                deckStatus={deckStatus}
                isCover={deckCard.scryfall_id === coverCardId}
                onSetCover={onSetCover}
                onSetCommander={onSetCommander}
                onCardSelect={onCardSelect}
                isSelectionMode={isSelectionMode}
                isSelected={selectedDeckCardIds?.has(deckCard.id)}
                onToggleSelect={onToggleDeckCard}
              />
            ) : (
              <div key={deckCard.id} className="aspect-488/680 animate-pulse rounded-xl bg-zinc-800/50" />
            ),
          )}
        </div>
      )}
    </section>
  );
}
